import React, { useMemo } from "react";
import { Html } from "@react-three/drei";
import { useModelStore } from "../../../../store/useModelStore";
import { useUIStore } from "../../../../store/useUIStore";
import { getScaleFromRange, scaleToGrid } from "@/utils/gridScale";

export const SelectedBeamIDLabels: React.FC = () => {
  // ✅ HOOKS FIRST — always
  const nodes = useModelStore((state) => state.nodes);
  const beams = useModelStore((state) => state.beams);
  const selectedBeamIds = useModelStore((state) => state.selectedBeamIds);
  const showBeamIds = useUIStore((state) => state.showBeamIds);
  const scaleRange = useUIStore((state) => state.scaleRange);

  const scaleFactor = getScaleFromRange(scaleRange);

  // ✅ Pre-scale nodes (same logic as nodes + camera)
  const scaledNodes = useMemo(() => {
    return nodes.map((n) => ({
      ...n,
      x: scaleToGrid(n.x, scaleFactor),
      y: scaleToGrid(n.y, scaleFactor),
      z: scaleToGrid(n.z, scaleFactor),
    }));
  }, [nodes, scaleFactor]);

  // ❗ Safe early exits AFTER hooks
  if (showBeamIds) return null;
  if (!selectedBeamIds.length || !beams.length || !nodes.length) return null;

  const selectedBeams = beams.filter((b) =>
    selectedBeamIds.includes(b.id)
  );

  return (
    <>
      {selectedBeams.map((beam) => {
        const startNode = scaledNodes.find(
          (n) => n.id === beam.startNodeId
        );
        const endNode = scaledNodes.find(
          (n) => n.id === beam.endNodeId
        );

        if (!startNode || !endNode) return null;

        // ✅ Midpoint in scaled space
        const midX = (startNode.x + endNode.x) / 2;
        const midY = (startNode.y + endNode.y) / 2;
        const midZ = (startNode.z + endNode.z) / 2;

        return (
          <Html
            key={`selected-beam-label-${beam.id}`}
            position={[midX, midY, midZ + 1.2]}
            center
            zIndexRange={[0, 10]}
          >
            <div
              style={{
                pointerEvents: "none",
                userSelect: "none",
                fontFamily:
                  "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial",
                fontSize: "16px",
                fontWeight: 600,
                color: "#dc2626",
                textShadow: "0 0 2px white, 0 0 4px white",
                whiteSpace: "nowrap",
              }}
            >
              B{beam.id}
            </div>
          </Html>
        );
      })}
    </>
  );
};
